import { ReactElement } from 'react';

import { appColors } from '@backrs_ui/common-ui/src/assets/appColors';
import FallbackBanner from '@backrs_ui/common-ui/src/assets/images/profile-header-fallback.png';
import { BackrsUserBadge } from '@backrs_ui/common-ui/src/atoms/BackrsUserBadge';
import { AppIcon, AppIcons, ICON_SIZES } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { ProfileWidgetProps } from './NuggetProfile';
import { BaseNugget } from '../BaseNugget';

const INFO_CONFIG: {
  key: Extract<keyof ProfileWidgetProps, 'jobTitle' | 'employer' | 'school' | 'grade'>;
  icon: AppIcons;
}[] = [
  {
    key: 'jobTitle',
    icon: 'Briefcase01',
  },
  {
    key: 'employer',
    icon: 'Building06',
  },
  {
    key: 'school',
    icon: 'Bank',
  },
  {
    key: 'grade',
    icon: 'Bank',
  },
];

export const NuggetProfilePanel = ({
  abbreviatedName,
  jobTitle,
  employer,
  school,
  grade,
  profileImageUrl,
  profileCoverImageUrl,
  role,
  linkToProfile,
  sharedTraitsText,
  onClick,
  subText,
  fitContent = false,
}: ProfileWidgetProps): ReactElement => {
  return (
    <BaseNugget subText={subText || sharedTraitsText} onClick={onClick} fitContent={fitContent}>
      <Box sx={contentSx}>
        <Box sx={headerContainerSx}>
          <Box sx={bannerImageContainer}>
            <Box component="img" src={profileCoverImageUrl || FallbackBanner} sx={bannerImageSx} />
          </Box>
          <Box sx={smallProfileImageContainerStyle}>
            <Box sx={profileImageWrapperStyle}>
              {profileImageUrl ? (
                <Box component="img" src={profileImageUrl} sx={profileImgSx} />
              ) : (
                <AppIcon iconName="User02" diameter="64px" color="currentColor" />
              )}
              {role && <BackrsUserBadge variant={role} />}
            </Box>
            <Typography variant="text-lg-semibold" sx={abbreviatedNameStyle}>
              {abbreviatedName}
            </Typography>
          </Box>
          {linkToProfile && (
            <Box
              sx={linkToProfileSx}
              onClick={(e) => {
                e.stopPropagation();
                window.open(linkToProfile, '_blank');
              }}
            >
              <Typography variant="text-sm-regular" sx={linkToProfileTextSx}>
                Profile
              </Typography>
              <AppIcon iconName="ArrowUpRight" diameter={ICON_SIZES['16px']} color="currentColor" />
            </Box>
          )}
        </Box>

        <Box sx={smallProfileInfoContainerStyle}>
          {INFO_CONFIG.map(({ key, icon }) => {
            const props = { jobTitle, employer, school, grade };
            const displayValue = props[key];

            return displayValue ? (
              <Box key={key} sx={infoRowStyle}>
                <AppIcon iconName={icon} diameter={ICON_SIZES['16px']} color="currentColor" />
                <Typography variant="text-sm-regular" sx={profileAttributeTextSx}>
                  {displayValue}
                </Typography>
              </Box>
            ) : null;
          })}
        </Box>
      </Box>
    </BaseNugget>
  );
};

const linkToProfileSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: backrs.spacing['spacing-xl'],
  right: backrs.spacing['spacing-xl'],
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xxs'],
  padding: `${backrs.spacing['spacing-xxs']} ${backrs.spacing['spacing-xs']}`,
  border: `1px solid ${backrs.colors['Gray/800']}`,
  borderRadius: backrs.radius['radius-sm'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  color: backrs.colors['Colors/Text/text-secondary'],
  cursor: 'pointer',
});

const linkToProfileTextSx: SxProps<Theme> = ({ typography }) => ({
  fontWeight: typography.fontWeightMedium,
});

const profileImgSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  aspectRatio: '1 / 1',
  borderRadius: backrs.radius['radius-full'],
  border: `2px solid ${backrs.colors['Colors/Base/Black']}`,
  width: '67px',
  height: '67px',
});

const abbreviatedNameStyle: SxProps<Theme> = {
  fontSize: '12pt',
  lineHeight: 1,
  fontWeight: 700,
  color: appColors.color.navy3,
};

const contentSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
};

const headerContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  height: '120px',
};

const smallProfileImageContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  left: backrs.spacing['spacing-lg'],
  top: backrs.spacing['spacing-7xl'],
  zIndex: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-end',
  gap: backrs.spacing['spacing-lg'],
});

const profileImageWrapperStyle: SxProps<Theme> = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
};

const bannerImageContainer: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
};

const bannerImageSx: SxProps<Theme> = {
  width: '100%',
  height: '85px',
  minHeight: '85px',
  maxHeight: '85px',
  objectFit: 'cover',
};

const smallProfileInfoContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: backrs.spacing['spacing-lg'],
  paddingTop: backrs.spacing['spacing-4xl'],
  flex: 1,
  gap: backrs.spacing['spacing-sm'],
});

const infoRowStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const profileAttributeTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/900'],
  alignSelf: 'flex-start',
});
